import type { Deck, DeckState, Navigation, Slide } from "./types";
import { stateAt, navigate } from "./state";
import { element, mountComponent, updateComponent, renderPassages, applyStyle } from "./components";

const sections = [...document.querySelectorAll<HTMLElement>(".ss-slide")];
function readDeck(): Deck {
  const script = document.querySelector("#ss-deck-data");
  const deck: Deck = script ? JSON.parse(script.textContent || "{}") : {
    version: 1, metadata: { title: document.title, history: "none" },
    slides: sections.map((section, i): Slide => ({ id: "slide-" + (i + 1), title: section.querySelector("h1")?.textContent ?? "",
      layout: "default", body: "", content: [], steps: [], style: {}, history: "none" }))
  };
  if (deck.version !== 1 || !Array.isArray(deck.slides) || deck.slides.length !== sections.length)
    throw new Error("Unsupported or inconsistent Slide Studio deck data");
  sections.forEach((section, i) => {
    const slide = deck.slides[i];
    section.id = slide.id; section.tabIndex = -1;
    section.querySelector<HTMLElement>(".ss-content > h1")?.setAttribute("data-ss-id", "title");
    section.querySelector<HTMLElement>(".ss-body")?.setAttribute("data-ss-id", "body");
    section.querySelector<HTMLElement>(".ss-body img")?.setAttribute("data-ss-id", "media");
    // Legacy fragment slides remain navigable. New steps precede legacy fragments
    // when mixed; prefer one reveal vocabulary within a slide.
    section.querySelectorAll<HTMLElement>(".fragment,[data-reveal]").forEach((fragment, j) => {
      fragment.classList.remove("fragment"); fragment.removeAttribute("data-reveal");
      const id = "legacy-fragment-" + j; fragment.dataset.ssId = id;
      slide.steps.push({ actions: [{ type: "reveal", target: id, effect: "fade-up" }] });
    });
    for (const node of slide.content) {
      const host = section.querySelector<HTMLElement>('[data-ss-id="' + CSS.escape(node.id) + '"]');
      if (host) mountComponent(node, host);
    }
    applyStyle(section, slide.style);
  });
  return deck;
}
const deck = readDeck();
const hashPosition = () => location.hash.slice(1).split("/").map(Number);
const [initialSlide, initialStep] = hashPosition();
let current = stateAt(deck, initialSlide, initialStep);
const status = element("div", "ss-roll-status");
status.setAttribute("aria-live", "polite"); document.querySelector(".ss-deck")?.append(status);
const progress = element("div", "ss-step-progress"); document.querySelector(".ss-deck")?.append(progress);
function render(state: DeckState) {
  const section = sections[state.slideIndex], slide = deck.slides[state.slideIndex];
  if (!section || !slide) return;
  sections.forEach((item, i) => { item.inert = i !== state.slideIndex; item.setAttribute("aria-hidden", String(i !== state.slideIndex)); });
  for (const node of slide.content) {
    const host = section.querySelector<HTMLElement>('[data-ss-id="' + CSS.escape(node.id) + '"]');
    if (host) updateComponent(node, host, state);
  }
  section.querySelectorAll<HTMLElement | SVGElement>("[data-ss-id]").forEach(target => {
    const value = state.componentState[target.dataset.ssId!];
    target.classList.toggle("ss-roll-hidden", value?.visible === false);
    target.setAttribute("aria-hidden", String(value?.visible === false));
    if (target instanceof HTMLElement) target.inert = value?.visible === false;
    target.dataset.effect = value?.effect ?? "";
    target.dataset.tone = value?.tone ?? "";
  });
  section.querySelector(".ss-action-callouts")?.remove();
  const callouts = element("div", "ss-action-callouts");
  for (const [id, value] of Object.entries(state.componentState)) if (value.callout)
    callouts.append(element("aside", "ss-callout", value.callout));
  if (callouts.children.length) section.append(callouts);
  renderPassages(section, slide, state);
  status.textContent = "Slide " + (state.slideIndex + 1) + " / " + deck.slides.length +
    " · Step " + state.stepIndex + " / " + slide.steps.length;
  progress.style.width = (slide.steps.length ? state.stepIndex / slide.steps.length * 100 : 0) + "%";
  try { history.replaceState(null, "", "#" + state.slideIndex + "/" + state.stepIndex); } catch { /* file origin */ }
}
export const roll = {
  deck,
  get state() { return current; },
  rehydrate(index: number) {
    const section = sections[index], slide = deck.slides[index];
    if (!section || !slide) return;
    section.querySelector(".ss-content > h1")?.setAttribute("data-ss-id", "title");
    section.querySelector(".ss-body")?.setAttribute("data-ss-id", "body");
    section.querySelector(".ss-body img")?.setAttribute("data-ss-id", "media");
    for (const node of slide.content) {
      const host = section.querySelector<HTMLElement>('[data-ss-id="' + CSS.escape(node.id) + '"]');
      if (host?.querySelector(".ss-component-fallback")) mountComponent(node, host);
    }
    applyStyle(section, slide.style);
    render(current);
  },
  dispatch(command: Navigation) {
    const before = current.slideIndex; current = navigate(deck, current, command); render(current);
    if (before !== current.slideIndex && window.parent === window) sections[current.slideIndex]?.focus({ preventScroll: true });
    return current;
  },
  refresh() { render(current); }
};
render(current);
(window as unknown as { SlideStudio: typeof roll }).SlideStudio = roll;
